import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChefHat, ArrowLeft, MailCheck } from "lucide-react";
import { BrandMark } from "@/components/Brand";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't process request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[hsl(var(--muted))]">
        <img src="https://images.pexels.com/photos/5792480/pexels-photo-5792480.jpeg" className="absolute inset-0 w-full h-full object-cover opacity-85" alt="ingredients" />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsla(20,3%,17%,0.45)] to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center"><BrandMark size={36}/></div>
          </Link>
          <div>
            <p className="heading text-3xl font-semibold max-w-md leading-tight">
              Locked out? We'll get you back to fresh meals in under a minute.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="forgot-back-home">
            <ArrowLeft size={14}/> Home
          </Link>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary ml-3" data-testid="back-to-login">
            ← Back to sign in
          </Link>

          {!sent ? (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
              <div>
                <h1 className="heading text-3xl font-bold">Forgot your password?</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email. Your admin will be notified and will share a one-time password with you via WhatsApp or phone.
                </p>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  required type="email" value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                />
              </div>

              <Button
                type="submit" disabled={busy}
                className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="forgot-submit"
              >
                {busy ? "Sending..." : "Send reset link"}
              </Button>

              <p className="text-xs text-muted-foreground">
                For security, we'll always show "email sent" even if no account uses that address.
              </p>
            </form>
          ) : (
            <div className="space-y-5 text-center" data-testid="forgot-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-secondary/10 flex items-center justify-center">
                <MailCheck size={26} className="text-secondary"/>
              </div>
              <div>
                <h1 className="heading text-2xl font-bold">Request sent</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  If <b>{email}</b> is registered, our admin team has been notified.
                  They'll share a one-time password with you via WhatsApp or phone shortly.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Made a typo? <button type="button" onClick={()=>setSent(false)} className="text-primary font-medium underline">try a different email</button>.
              </p>
              <Link to="/login">
                <Button variant="outline" className="rounded-full">Back to sign in</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
